import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule, registerLazyOrchestrator } from 'owa-bundling';
import markItemsAsReadStoreUpdate from 'owa-mail-actions/lib/triage/markItemsAsReadStoreUpdate';
import initializeExtendedCardForItemReadingPane from './actions/initializeExtendedCardForItemReadingPane';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "ReadingPaneStore"*/ './lazyIndex'),
    { name: 'ReadingPaneStore' }
);

// Export delay loaded actions
export const lazyPrint: LazyAction<typeof lazyIndex.print, typeof lazyModule> = new LazyAction(
    lazyModule,
    m => m.print
);
export const lazyLoadItemReadingPane: LazyAction<
    typeof lazyIndex.loadItemReadingPane,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.loadItemReadingPane);
export const lazyLoadItemReadingPaneForSingleMailItemSelected: LazyAction<
    typeof lazyIndex.loadItemReadingPaneForSingleMailItemSelected,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.loadItemReadingPaneForSingleMailItemSelected);
export const lazyLoadItemState: LazyAction<typeof lazyIndex.loadItemState, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.loadItemState);

// Register lazy orchestrator
registerLazyOrchestrator(
    markItemsAsReadStoreUpdate,
    lazyModule,
    m => m.markItemsAsReadStoreUpdateOrchestrator
);
registerLazyOrchestrator(
    initializeExtendedCardForItemReadingPane,
    lazyModule,
    m => m.initializeExtendedCardForItemReadingPaneOrchestrator
);

export const lazyIsUnsupportedItem: LazyImport<
    typeof lazyIndex.isUnsupportedItem,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.isUnsupportedItem);

export const lazyReplyByMeeting: LazyAction<typeof lazyIndex.replyByMeeting, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.replyByMeeting);
export const lazyToggleUndoDarkMode: LazyAction<
    typeof lazyIndex.toggleUndoDarkMode,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.toggleUndoDarkMode);
export const lazySetHidePopout: LazyAction<typeof lazyIndex.setHidePopout, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.setHidePopout);
export const lazySetCalendarEventResponseTypeMutator: LazyAction<
    typeof lazyIndex.setCalendarEventResponseTypeMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setCalendarEventResponseTypeMutator);
export const lazyCreateThreadSummarizationViewState: LazyAction<
    typeof lazyIndex.createThreadSummarizationViewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.createThreadSummarizationViewState);
export const lazySetAddinDecryptionTakingLongerInfoBarVisibilityMutator: LazyAction<
    typeof lazyIndex.setAddinDecryptionTakingLongerInfoBarVisibilityMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setAddinDecryptionTakingLongerInfoBarVisibilityMutator);
export const lazySetAddinDecryptionSuccessInfoBarVisibilityMutator: LazyAction<
    typeof lazyIndex.setAddinDecryptionSuccessInfoBarVisibilityMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setAddinDecryptionSuccessInfoBarVisibilityMutator);
// Export synchronous actions
export { isItemHeldByConversationItemParts, isItemHeldByItemReadingPane } from './utils/isItemHeld';
export { isLocallyParsedMessageFile } from './utils/messageFileUtils';

// Export store and schema
export type { default as ItemViewState } from './store/schema/ItemViewState';
export { default as readingPaneStore } from './store/Store';
export type { default as SmartPillViewState } from './store/schema/SmartPillViewState';
export type { default as ItemReadingPaneViewState } from './store/schema/ItemReadingPaneViewState';
export type { default as ItemPartViewState } from './store/schema/ItemPartViewState';
export type { default as DeeplinkId } from './store/schema/DeeplinkId';
export type { default as ActionableMessageCardInItemViewState } from './store/schema/ActionableMessageCardInItemViewState';
export { default as getConversationReadingPaneViewState } from './utils/getConversationReadingPaneViewState';
export { default as getItemReadingPaneViewState } from './utils/getItemReadingPaneViewState';
export type { default as LoadItemOptions } from './store/schema/LoadItemOptions';
export type { default as ReadingPaneViewState } from './store/schema/ReadingPaneViewState';
